/**
 * Client-side mirror of the backend's `getRawOutput()` helper
 * (see Backend/functions/src/modelrun/modelTypes.ts:446).
 *
 * `rawOutputFull` is the source of truth on every modern ModelOutput row;
 * the deprecated `rawOutput` field is dropped on new writes. The admin UI
 * therefore must derive the visible-answer text from `rawOutputFull`
 * (provider-wrapped JSON) instead of reading `rawOutput` directly.
 *
 * Mirrors the on-disk shape from
 * Backend/functions/src/modelrun/llmCall/rawOutputFull.ts and the
 * provider-specific text-extraction switch in modelTypes.ts. Kept in
 * lockstep with those files — when a new provider lands, add the case
 * here too.
 *
 * Structurally typed: accepts anything with optional `rawOutputFull` /
 * `rawOutput` string fields, so callers can pass either the admin
 * `ModelOutput` shape or the backend's without coupling.
 */

type RawOutputFullProvider =
    | 'anthropic'
    | 'openai-chat'
    | 'openai-responses'
    | 'gemini'
    | 'mock';

interface RawOutputFull {
    schemaVersion: 1;
    provider: RawOutputFullProvider;
    raw: unknown;
}

interface OutputLike {
    rawOutputFull?: string;
    rawOutput?: string;
}

function parseRawOutputFull(s: string | undefined | null): RawOutputFull | null {
    if (!s) return null;
    try {
        const v = JSON.parse(s);
        if (
            v
            && typeof v === 'object'
            && (v as { schemaVersion?: unknown }).schemaVersion === 1
            && typeof (v as { provider?: unknown }).provider === 'string'
        ) {
            return v as RawOutputFull;
        }
    } catch {
        // not JSON — legacy or corrupt; caller falls through
    }
    return null;
}

function extractResponsesText(raw: unknown): string {
    if (!raw) return '';
    if (typeof raw === 'object' && raw !== null) {
        const conv = (raw as { output_text?: unknown }).output_text;
        if (typeof conv === 'string' && conv.length > 0) return conv;
    }
    const items = Array.isArray(raw) ? raw : (raw as { output?: unknown }).output;
    if (!Array.isArray(items)) return '';
    const texts: string[] = [];
    for (const it of items) {
        if (!it || typeof it !== 'object') continue;
        const item = it as { type?: unknown; content?: unknown };
        if (item.type !== 'message') continue;
        const content = item.content;
        if (!Array.isArray(content)) continue;
        for (const c of content) {
            if (!c || typeof c !== 'object') continue;
            const part = c as { type?: unknown; text?: unknown };
            if ((part.type === 'output_text' || part.type === 'text') && typeof part.text === 'string') {
                texts.push(part.text);
            }
        }
    }
    return texts.join('');
}

function extractFromRawOutputFull(rof: RawOutputFull): string {
    const raw = rof.raw as Record<string, unknown> | null | undefined;
    if (!raw || typeof raw !== 'object') return '';
    switch (rof.provider) {
        case 'anthropic': {
            const blocks = (raw as { content?: unknown }).content;
            if (!Array.isArray(blocks)) return '';
            return blocks
                .filter((b): b is { type: string; text: string } =>
                    !!b && typeof b === 'object'
                    && (b as { type?: unknown }).type === 'text'
                    && typeof (b as { text?: unknown }).text === 'string')
                .map(b => b.text)
                .join('');
        }
        case 'openai-chat': {
            const choices = (raw as { choices?: unknown }).choices;
            if (!Array.isArray(choices) || choices.length === 0) return '';
            const msg = (choices[0] as { message?: { content?: unknown } }).message;
            return typeof msg?.content === 'string' ? msg.content : '';
        }
        case 'openai-responses': {
            return extractResponsesText(raw);
        }
        case 'gemini': {
            const candidates = (raw as { candidates?: unknown }).candidates;
            if (!Array.isArray(candidates) || candidates.length === 0) return '';
            const parts = (candidates[0] as { content?: { parts?: unknown } }).content?.parts;
            if (!Array.isArray(parts)) return '';
            return parts
                .filter((p): p is { text: string; thought?: boolean } =>
                    !!p && typeof p === 'object'
                    && typeof (p as { text?: unknown }).text === 'string'
                    && !(p as { thought?: unknown }).thought)
                .map(p => p.text)
                .join('');
        }
        case 'mock': {
            const text = (raw as { text?: unknown }).text;
            return typeof text === 'string' ? text : '';
        }
    }
    return '';
}

/** Derive the visible-answer text from a ModelOutput-like object. Tries the
 *  wrapped `rawOutputFull` envelope first, then unwrapped legacy Responses
 *  shapes on `rawOutputFull`, then the deprecated `rawOutput` field. Returns
 *  empty string when nothing is available. */
export function getRawOutputText(output: OutputLike | null | undefined): string {
    if (!output) return '';
    const rof = parseRawOutputFull(output.rawOutputFull);
    if (rof) {
        const text = extractFromRawOutputFull(rof);
        if (text) return text;
    }
    // Legacy: pre-S3 rows stored a non-wrapped raw response on rawOutputFull
    // (early OpenAI Responses rows held the full response object directly).
    if (output.rawOutputFull) {
        try {
            const parsed = JSON.parse(output.rawOutputFull) as unknown;
            const text = extractResponsesText(parsed);
            if (text) return text;
        } catch { /* not JSON — fall through */ }
    }
    // Final fallback: the deprecated rawOutput field (production rows pre-S3,
    // and non-LLM rows like labeled outputs that store assessment JSON here).
    return output.rawOutput ?? '';
}
